import { LowerCasePipe } from '@angular/common';
import { Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppInfo } from '../../../../../../../global/environment';
import { Tracking } from '../../../../../../../global/tracking/tracking';
import { ButtonComponent } from '../../../../../../../shared/components/button/button.component';
import { RadioButtonComponent } from '../../../../../../../shared/components/forms/radio-button/radio-button.component';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { VideoPlayerComponent } from '../smart-fix-gen-ai/smart-fix-chat/smart-fix-video-player/smart-fix-video-player.component';
import { SmartFixAnalyticsService } from '../smart-fix-gen-ai/utils/analytics/smart-fix-analytics-service';

export enum FilterFixApplianceType {
  TumbleDryer = 'tumble dryer',
  WashingMachine = 'washing machine',
  WasherDryer = 'washer dryer',
}

enum needARepairOptions {
  needsRepair = `Yes, I do`,
  doesNotNeedRepair = `No, I don't`,
}

@Component({
  selector: 'app-smart-fix-embedded-fix',
  imports: [FormsModule, RadioButtonComponent, LowerCasePipe, ButtonComponent, VideoPlayerComponent],
  templateUrl: './smart-fix-embedded-fix.component.html',
  styleUrl: './smart-fix-embedded-fix.component.scss',
})
export class SmartFixEmbeddedFix implements OnInit {
  repairOptions: string[] = [`Yes, I do`, `No, I don't`];

  showError: boolean = false;
  videoOpen: boolean = false;
  youtubeUrl: string = '';
  feedbackOptionSelected: string = '';
  cancelRepair = signal(false);
  videoTitle: string = '';
  videoOffset: any = '';

  private viewModel = computed(() => {
    const bundle = this.orbitFormClaimsService?.state?.claimData?.bundle;

    return {
      productType: bundle?.productType.toLowerCase(),
      planNumber: bundle?.planNumber,
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private orbitFormClaimsService: OrbitFormClaimService,
    private smartFixAnalyticsService: SmartFixAnalyticsService
  ) {}

  ngOnInit() {
    const productType = this.vm.productType;
    if (productType) {
      this.generateCorrectVideoURL(this.vm.productType as FilterFixApplianceType);
    }

    Tracking.capture('smart_fix_video_guide_viewed', {
      productType,
    });

    this.smartFixAnalyticsService.pageViewEvent('claims-sf-chatbot-embedded');
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'banner presented',
      '',
      'banner chatbot',
      'SF chatbot embedded',
      'claims-sf-chatbot-embedded'
    );
  }

  generateCorrectVideoURL(productType: FilterFixApplianceType): void {
    switch (productType) {
      case FilterFixApplianceType.TumbleDryer:
        this.youtubeUrl = 'https://www.youtube.com/embed/AhvWqIi0vsQ';
        this.videoTitle = 'Most common tumble dryer problems';
        this.videoOffset = '24';
        break;
      case FilterFixApplianceType.WasherDryer:
      case FilterFixApplianceType.WashingMachine:
        this.youtubeUrl = 'https://www.youtube.com/embed/Z7nC5ZiKntg';
        this.videoTitle = 'How to clean a washing machine';
        this.videoOffset = '20';
        break;
      default:
        break;
    }
  }

  onOpenVideoGuide() {
    this.videoOpen = true;

    Tracking.capture('smart_fix_video_guide_opened', {
      productType: this.vm.productType,
    });

    // Track click on CTA to open video guide
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'Yes',
      '',
      'banner chatbot',
      'SF chatbot embedded',
      'claims-sf-chatbot-embedded'
    );
  }

  // Track click on video
  onVideoClicked() {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      `video watched - ${this.videoTitle}`,
      '',
      'youtube video',
      'SF chatbot embeded',
      'claims-sf-chatbot-video'
    );
  }

  onVideoLoaded() {
    // Track video load
    this.smartFixAnalyticsService.pageViewEvent('claims-sf-chatbot-video');
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      `video presented - ${this.videoTitle}`,
      '',
      'youtube video',
      'SF chatbot embeded',
      'claims-sf-chatbot-video'
    );
  }

  onAnswerSelected(optionSelected: string): void {
    this.feedbackOptionSelected = optionSelected;
    this.showError = false;

    if (optionSelected === needARepairOptions.doesNotNeedRepair) {
      this.cancelRepair.set(true);
      Tracking.capture('smart_fix_video_guide_resolved', {
        productType: this.vm.productType,
      });
    } else {
      this.cancelRepair.set(false);
      Tracking.capture('smart_fix_video_guide_still_needs_repair', { productType: this.vm.productType });
    }
  }

  validateSelection(): boolean {
    if (!this.videoOpen) {
      return true;
    }

    if (!this.feedbackOptionSelected) {
      this.showError = true;
      return false;
    }

    return true;
  }

  handleValidationAndContinue(): boolean {
    if (!this.validateSelection()) return false;

    if (this.feedbackOptionSelected === needARepairOptions.doesNotNeedRepair) {
      this.handleNoRepairNeeded();
      if (this.videoOpen) {
        Tracking.capture('smart_fix_video_guide_resolved', { productType: this.vm.productType });
        this.sendGATag();
      }
      return false;
    }

    if (this.videoOpen) {
      Tracking.capture('smart_fix_video_guide_still_needs_repair', { productType: this.vm.productType });
      this.sendGATag();
    }
    return true;
  }

  // Just send them back to MMR
  handleNoRepairNeeded(): void {
    this.orbitFormClaimsService.removeCachedItems();
    window.location.href = `${AppInfo.Environment.claimsURL}/my-plans/${this.vm.planNumber}/claims?cancelRepair=true`;
  }

  sendGATag(): void {
    const eventLabel =
      this.feedbackOptionSelected === needARepairOptions.doesNotNeedRepair
        ? needARepairOptions.doesNotNeedRepair
        : needARepairOptions.needsRepair;

    this.smartFixAnalyticsService.triggerSmartFixEvent(
      eventLabel,
      '',
      'video - still need a repair',
      'SF chatbot embeded',
      'claims-sf-chatbot-video'
    );
  }
}
